import sxShouldForwardProp from '@styled-system/should-forward-prop';
import React, { Ref, useCallback } from 'react';
import { OmitStrict } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion';
import { CloseIcon as CloseIconSVG, SearchIcon as SearchIconSVG } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { TextInput } from '@tanium/react-text-input';
import { useControlledProp } from '@tanium/react-use-controlled-prop';
import { css, layout, LayoutStyleProps } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';
import { useTranslation } from './localization';
import { SearchIcon as SearchIconColor } from './themeDefinitions';

export interface FilterInputChangeEvent {
  value: string;
}

export interface FilterInputChangeEventHandler {
  (evt: FilterInputChangeEvent): void;
}

type TextInputProps = React.ComponentPropsWithoutRef<typeof TextInput>;

const inputSizes = ['sm', 'lg'] as const;
export type InputSize = (typeof inputSizes)[number];

export interface FilterInputProps
  extends OmitStrict<TextInputProps, 'onChange' | 'width' | 'size'>,
    Pick<LayoutStyleProps, 'width'> {
  /**
   * Fired whenever the content of the filter box is changed.
   */
  onChange?: FilterInputChangeEventHandler;

  /**
   * Size of the component. Use `sm` or `lg`. Defaults to `lg`.
   */
  size?: InputSize;
}

interface FilterInputComponentProps extends FilterInputProps {
  innerRef: Ref<HTMLInputElement>;
}

const ensureInputSize = (size: InputSize) => (inputSizes.includes(size) ? size : 'lg');

const iconAlignmentStyle = css({
  position: 'absolute',
  right: 1,
});

const heights: Record<InputSize, number> = {
  sm: 24,
  lg: 32,
};
const iconSizes: Record<InputSize, number> = {
  sm: 14,
  lg: 16,
};
const verticalPaddings: Record<InputSize, number> = {
  sm: 4,
  lg: 8,
};

export const StyledTextInput = styled(TextInput, { shouldForwardProp: (p) => p !== 'customSize' })<
  TextInputProps & { customSize: InputSize }
>(
  css({ width: '100%', paddingRight: ThemeSpace.minor[7] }),
  ({ customSize }) =>
    customSize === 'sm' && {
      height: `${heights[customSize]}px`,
      fontSize: '12px',
    },
  ({ customSize }) =>
    css({
      paddingTop: `${verticalPaddings[customSize]}px`,
      paddingBottom: `${verticalPaddings[customSize]}px`,
    }),
);

const AlignedIconButton = styled(IconButton, { shouldForwardProp: (p) => p !== 'size' })<{
  size: InputSize;
}>(iconAlignmentStyle, ({ size }) => ({
  top: `${(heights[size] - iconSizes[size]) / 2}px`,
}));

export const SearchIcon = styled(SearchIconSVG, { shouldForwardProp: (p) => p !== 'size' })<{
  size?: InputSize;
}>(iconAlignmentStyle, ({ size = 'lg' }) =>
  css({
    color: SearchIconColor,
    top: `${(heights[size] - iconSizes[size]) / 2}px`,
  }),
);

const FilterBoxWrapper = styled('span', {
  shouldForwardProp: sxShouldForwardProp,
})<LayoutStyleProps>(
  {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    ':focus': {
      outline: 0,
    },
  },
  layout,
);

const UnwrappedFilterInputComponent = ({
  // Props we manually spread
  className,
  innerRef,
  // Props we don't want spread
  defaultValue = '',
  // Style props to spread to wrapper
  width = '160px',
  // Other props
  onChange,
  onKeyDown,
  value: valueProp,
  size: sizeProp = 'lg',
  ...rest
}: FilterInputComponentProps) => {
  const size = ensureInputSize(sizeProp);
  const { t } = useTranslation();

  const [value, setValue] = useControlledProp({
    value: valueProp,
    defaultValue,
    componentName: 'FilterInput',
    propName: 'value',
    onUpdate: ({ value: newValue }) => {
      if (onChange) {
        onChange({
          value: newValue as string,
        });
      }
    },
  });

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: newValue } }) => {
      setValue(newValue);
    },
    [setValue],
  );

  const clearValue = useCallback(() => {
    setValue('');
  }, [setValue]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        clearValue();
      }
      onKeyDown?.(e);
    },
    [clearValue, onKeyDown],
  );

  return (
    <FilterBoxWrapper width={width}>
      <StyledTextInput
        {...rest}
        className={className}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value}
        ref={innerRef}
        customSize={size}
      />
      {value ? (
        <AlignedIconButton
          label={t('Clear text')}
          icon={CloseIconSVG}
          width={iconSizes[size]}
          height={iconSizes[size]}
          size={size}
          onClick={clearValue}
        />
      ) : (
        <SearchIcon
          width={iconSizes[size]}
          height={iconSizes[size]}
          size={size}
          role="presentation"
        />
      )}
    </FilterBoxWrapper>
  );
};

export const FilterInput = React.forwardRef<HTMLInputElement, FilterInputProps>((props, ref) => (
  <UnwrappedFilterInputComponent {...props} innerRef={ref} />
));

FilterInput.displayName = 'FilterInput';
